/**
 * E05-F09 — the "Test Prompt" window (PROTOTYPE, stack-native).
 *
 * Opened from the `Test` button in a Model Matrix row. It takes the values of a
 * REAL recorded run of that prompt, rebuilds the request from a draft template
 * the admin can edit right here, makes exactly one call, and shows the answer
 * three ways: as the user would see it, raw, and next to what the model actually
 * answered in production on the same data.
 *
 * Layout follows the spec (§ UI) verbatim:
 *   header  — run selector + prompt meta + the `web_search` badge;
 *   left    — the editable draft + this run's values with their sizes;
 *   centre  — the full prompt that will be sent, rebuilt as you type;
 *   bottom  — the three result tabs + model / tokens / duration;
 *   footer  — "Test" (marked as one paid call) and a SEPARATE "Save template".
 *
 * Two things this window deliberately does NOT do:
 *   - "Test" never writes the template. Tuning a prompt must not ship it to
 *     every user mid-iteration (spec decision 4). The draft travels in the body
 *     of `POST …/test`, which only READS `ai_prompts`; saving is its own button,
 *     wired to `useUpdatePrompt` (PATCH /admin/prompts/:key).
 *   - it never offers "test all prompts". Bulk AI runs are the exact shape of
 *     the two money leaks this project already paid for.
 *
 * The journal and the test call are the REAL endpoints (`promptRunsApi.ts` →
 * `real/promptRuns.ts`): one press of "Test" is one paid model call.
 *
 * COPY: strings live in `COPY` below rather than inline. This codebase has no
 * i18n runtime (no i18next, no locale files — every screen is hardcoded
 * English, and Russian copy leaking into the UI was logged as a P0 in the
 * 2026-07 fidelity audit), so the window follows that convention; `COPY` is the
 * single seam an i18n pass would replace.
 */
import { useEffect, useMemo, useState, type JSX } from 'react';
import type { AdminPromptDTO } from '@eer/shared';
import { Dialog, useToast } from '../ui/index.js';
import { errorMessage } from '../api/client.js';
import { DocumentView } from '../document/DocumentView.js';
import { buildAnalysisDetail, buildPreKickoffBrief } from '../prekickoff/prekickoffModel.js';
import { useUpdatePrompt } from './promptsApi.js';
import { usePromptRun, usePromptRuns, useTestPrompt } from './promptRunsApi.js';
import {
  asPreKickoffAnalysis,
  chooseUserTabContent,
  describeAnswer,
  findMissingVars,
  findUnusedVars,
  formatChars,
  formatDurationOrDash,
  formatParsed,
  formatTokens,
  parsedEntries,
  previewItemLabel,
  recordedRunStats,
  runLabel,
  runStatus,
  substitutePromptVars,
  testCallStats,
  usesWebSearch,
  varsToMap,
  type PromptAnswerState,
  type PromptCallStats,
  type PromptRunDetail,
  type PromptTestPreview,
  type PromptTestPreviewItem,
  type PromptTestResult,
  type UserTabContent,
} from './promptTestModel.js';

const COPY = {
  title: 'Test prompt',
  runSelector: 'Recorded run',
  runOrigin: { production: 'production', test: 'test replay' },
  emptyTitle: 'This prompt has never been run',
  emptyBody:
    'The journal has no recorded call for this prompt yet, so there are no input values to ' +
    'replay. A run is written the first time the live app uses this prompt — on production ' +
    'that is the next real project; on dev, the next time the flow is exercised there.',
  emptyHint: 'Nothing to test against, so the run button stays disabled.',
  draft: 'Prompt template (draft)',
  draftHint: 'Edited here only. "Test" runs this text; it is not saved until you press Save.',
  values: 'Values recorded in this run',
  valuesEmpty: 'This run recorded no variables.',
  truncated: (n: number) => `+${n} chars truncated by the recorder`,
  rendered: 'Full prompt that will be sent',
  renderedHint: 'Rebuilt on every keystroke — draft template + the values above.',
  sentThen: 'Show the prompt that was actually sent in this run',
  missingTitle: 'Values missing from this run',
  missingBody:
    'The draft uses these placeholders and this run has no data for them. They will reach the ' +
    'model as literal text, so the answer will look wrong for a reason that is not the prompt.',
  unusedTitle: 'Recorded but no longer used',
  run: 'Test',
  running: 'Testing…',
  runNote: '1 paid call',
  save: 'Save template',
  saving: 'Saving…',
  saved: 'Prompt template saved',
  close: 'Close',
  webSearch: 'web_search',
  webSearchHint: 'This prompt goes out to the web: the call costs more and takes minutes.',
  tabs: {
    user: 'As the user sees it',
    raw: 'Raw answer',
    production: 'What production got',
  },
  resultPlaceholder: 'Press "Test" to run this draft against the selected run.',
  callFailed: 'The call failed',
  callFailedHint:
    'One click is one call — there is no retry and no failover to the second provider.',
  productionFailedHint: 'This is what the recorded run ended with — no answer was ever stored.',
  parseFailed: 'The answer could not be parsed',
  parseFailedHint:
    'The model answered, but the standard parser could not read it. On the live screen this is ' +
    'what shows up as an empty result.',
  parseNotApplicable: 'Plain-text prompt — nothing to parse.',
  answerEmpty: 'The model returned an empty answer',
  answerEmptyHint:
    'The call went through and was paid for; the answer itself is blank. That is the defect — ' +
    'not a gap in this window.',
  answerNone: 'No answer is stored for this run.',
  noParsed: 'Nothing was parsed, so there is nothing to render as the user would see it.',
  docPicker: 'Artifact',
  /**
   * WHOSE cleanup produced what is on screen. Said out loud on purpose: the value
   * of this tab is that the answer went through the SAME parse and cleanup the
   * live pipeline runs, and an unlabelled rendering is indistinguishable from a
   * pretty-printer of the raw JSON — which is exactly the thing it must not be
   * mistaken for.
   */
  pipeline: (name: string) => `cleaned by ${name}`,
  previewEmpty:
    'The production cleanup read nothing renderable out of this answer, so the live screen would ' +
    'have shown nothing here either.',
  previewTextEmpty:
    'The cleaned answer is blank, so the field this text is written into would stay empty on the ' +
    'live screen.',
  analysisInvalid:
    'The cleaned analysis does not match the shape the Pre-Kickoff panel reads, so it is listed as ' +
    'fields rather than drawn as half a brief.',
  analysisNoData: 'Not filled in by the model.',
  stats: { model: 'Model', tokens: 'Tokens', duration: 'Time', origin: 'Origin' },
  /**
   * Whose numbers are on screen. The two sides of the comparison ran at
   * different times on possibly different models, so the stats line always says
   * which call it belongs to.
   */
  statsSide: { test: 'this test call', production: 'recorded run' },
} as const;

/**
 * `origin` arrives as a PLAIN STRING (the read contract keeps it open so a row
 * written by an older code path can never 500 the whole journal), so it is
 * matched rather than indexed — an unknown label is shown as-is instead of
 * being silently relabelled "production".
 */
function originLabel(origin: string): string {
  if (origin === 'test') return COPY.runOrigin.test;
  if (origin === 'production') return COPY.runOrigin.production;
  return origin;
}

const PANEL = 'rounded-lg border border-border bg-white';
const PANEL_HEAD =
  'flex items-center justify-between gap-2 border-b border-border bg-surface-muted px-3 py-2';
const PANEL_TITLE = 'text-[11px] font-semibold uppercase tracking-wide text-text-strong';
const MONO = 'font-mono text-[11px] leading-relaxed';

// ---------------------------------------------------------------------------
// Small parts
// ---------------------------------------------------------------------------

function Badge({
  tone,
  children,
  testId,
  title,
}: {
  readonly tone: 'ok' | 'warn' | 'danger' | 'info' | 'muted';
  readonly children: React.ReactNode;
  readonly testId?: string;
  readonly title?: string;
}): JSX.Element {
  const cls: Record<typeof tone, string> = {
    ok: 'bg-success/15 text-success',
    warn: 'bg-warning/20 text-[color:var(--color-warning)]',
    danger: 'bg-danger/15 text-danger',
    info: 'bg-info/15 text-info',
    muted: 'bg-surface-muted text-text-muted',
  };
  return (
    <span
      data-testid={testId}
      title={title}
      className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${cls[tone]}`}
    >
      {children}
    </span>
  );
}

/** One recorded `{{variable}}`: name, size, truncation flag, collapsible value. */
function VarRow({
  name,
  value,
  chars,
  truncatedChars,
}: {
  readonly name: string;
  readonly value: string;
  readonly chars: number;
  readonly truncatedChars: number | null;
}): JSX.Element {
  return (
    <details
      data-testid="prompt-test-var"
      data-var-name={name}
      className="group border-b border-border last:border-b-0"
    >
      <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 hover:bg-surface-muted">
        <span className="font-mono text-[11px] text-text-strong">{`{{${name}}}`}</span>
        <span className="flex items-center gap-2">
          {truncatedChars !== null ? (
            <Badge
              tone="warn"
              testId="prompt-test-var-truncated"
              title={COPY.truncated(truncatedChars)}
            >
              truncated
            </Badge>
          ) : null}
          <span data-testid="prompt-test-var-size" className="text-[10px] text-text-muted">
            {formatChars(chars)}
          </span>
        </span>
      </summary>
      <pre
        className={`max-h-48 overflow-auto whitespace-pre-wrap break-words bg-surface-muted px-3 py-2 text-text ${MONO}`}
      >
        {value}
      </pre>
    </details>
  );
}

/**
 * The pre-kickoff brief, drawn from the SAME view model the live Pre-Kickoff
 * panel is built on (`buildPreKickoffBrief` + `buildAnalysisDetail`). Reusing the
 * model rather than the panel component is deliberate: `PreKickoffPanel` is bound
 * to a project (it edits, applies and sends), while the field selection, the
 * ordering and the drop-empty-rows rule — everything that decides WHAT the
 * recruiter reads — lives in the model, so a change there moves both screens.
 */
function AnalysisPreview({ content }: { readonly content: unknown }): JSX.Element {
  const analysis = asPreKickoffAnalysis(content);
  if (!analysis) {
    return (
      <div data-testid="prompt-test-user-structured" className="grid gap-3">
        <p
          data-testid="prompt-test-analysis-invalid"
          className="rounded border-l-4 border-warning bg-warning/10 px-3 py-2 text-xs text-text"
        >
          {COPY.analysisInvalid}
        </p>
        <StructuredFields parsed={content} />
      </div>
    );
  }

  const brief = buildPreKickoffBrief(analysis);
  const detail = buildAnalysisDetail(analysis);

  return (
    <div data-testid="prompt-test-user-analysis" className="grid gap-3">
      {brief.summary.trim() !== '' ? (
        <p className="whitespace-pre-wrap break-words text-sm text-text">{brief.summary}</p>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        {(
          [
            { testId: 'prompt-test-brief-company', title: 'Company', fields: brief.company },
            { testId: 'prompt-test-brief-position', title: 'Position', fields: brief.position },
          ] as const
        ).map((card) => (
          <div
            key={card.testId}
            data-testid={card.testId}
            className="rounded border border-border p-4"
          >
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
              {card.title}
            </h4>
            {card.fields.length === 0 ? (
              <p className="text-sm text-text-muted">{COPY.analysisNoData}</p>
            ) : (
              <dl className="space-y-1">
                {card.fields.map((f) => (
                  <div key={f.label} className="flex gap-2 text-sm">
                    <dt className="w-28 shrink-0 font-medium text-text-muted">{f.label}</dt>
                    <dd className="text-slate-700">{f.value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        ))}
      </div>
      {detail.length > 0 ? (
        <dl data-testid="prompt-test-analysis-detail" className="grid gap-2">
          {detail.map((s) => (
            <div key={s.label} className="border-t border-border pt-2">
              <dt className="text-[11px] font-medium uppercase tracking-wide text-text-muted">
                {s.label}
              </dt>
              <dd className="mt-0.5 text-sm text-text">
                {s.items ? (
                  <ul className="list-disc pl-5">
                    {s.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <span className="whitespace-pre-wrap break-words">{s.value}</span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  );
}

/** Top-level fields of a cleaned result — the shape used when there is no screen. */
function StructuredFields({ parsed }: { readonly parsed: unknown }): JSX.Element {
  const entries = parsedEntries(parsed);
  if (entries.length === 0) {
    return <pre className={`whitespace-pre-wrap break-words ${MONO}`}>{formatParsed(parsed)}</pre>;
  }
  return (
    <dl className="grid gap-2">
      {entries.map((e) => (
        <div key={e.key} className="border-b border-border pb-2" data-field={e.key}>
          <dt className="text-[11px] font-medium text-text-muted">{e.key}</dt>
          <dd className={`mt-0.5 whitespace-pre-wrap break-words text-text ${MONO}`}>{e.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * ONE piece of the cleaned answer, drawn the way the live screen draws it.
 *
 * `document` and `email` go through the very `DocumentView` the project card
 * uses — same renderer, same doc types, so a document that renders badly here
 * renders badly there too (which is the finding).
 */
function PreviewItemView({ item }: { readonly item: PromptTestPreviewItem }): JSX.Element {
  if (item.view === 'document' || item.view === 'email') {
    return (
      <div data-testid={`prompt-test-user-${item.view}`} data-doc-type={item.docType}>
        <DocumentView docType={item.docType} content={item.content} />
      </div>
    );
  }

  if (item.view === 'analysis') {
    return <AnalysisPreview content={item.content} />;
  }

  if (item.view === 'text') {
    const text = typeof item.content === 'string' ? item.content : formatParsed(item.content);
    // An empty plain-text result is a FINDING (the live field would stay blank),
    // so it is said, never rendered as a blank box.
    return text.trim() === '' ? (
      <p data-testid="prompt-test-user-text-empty" className="text-xs text-text-muted">
        {COPY.previewTextEmpty}
      </p>
    ) : (
      <pre
        data-testid="prompt-test-user-text"
        className="whitespace-pre-wrap break-words text-sm text-text"
      >
        {text}
      </pre>
    );
  }

  // No screen of its own → the CLEANED parsed result as fields, never nothing.
  return (
    <div data-testid="prompt-test-user-structured">
      <StructuredFields parsed={item.content} />
    </div>
  );
}

/**
 * The "As the user sees it" tab: the answer after the PRODUCTION parse and
 * cleanup of this prompt (`result.preview`, built server-side by calling the
 * pipelines' own functions), split into the pieces the live screens draw.
 *
 * One call can produce several pieces — four documents, or the pre-kickoff brief
 * plus three e-mail drafts — and on the live screens those are separate
 * artifacts, so they get a picker here rather than being stacked into one panel.
 */
function UserResultView({
  preview,
  parsed,
}: {
  readonly preview: PromptTestPreview | null;
  readonly parsed: unknown;
}): JSX.Element {
  const items = preview?.items ?? [];
  const [index, setIndex] = useState(0);
  const active = items[Math.min(index, Math.max(items.length - 1, 0))];

  if (!preview) {
    // No cleanup ran (older server / nothing to clean) — the generic parse is
    // still shown as fields rather than an empty panel.
    return parsed === null || parsed === undefined ? (
      <p data-testid="prompt-test-user-empty" className="text-xs text-text-muted">
        {COPY.noParsed}
      </p>
    ) : (
      <div data-testid="prompt-test-user-structured">
        <StructuredFields parsed={parsed} />
      </div>
    );
  }

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {items.length > 1 ? (
          <label className="flex items-center gap-2 text-xs text-text-muted">
            {COPY.docPicker}
            <select
              data-testid="prompt-test-doc-select"
              className="rounded border border-border px-2 py-1 text-xs"
              value={String(Math.min(index, items.length - 1))}
              onChange={(e) => setIndex(Number(e.target.value))}
            >
              {items.map((it, i) => (
                <option key={`${it.docType}-${i}`} value={String(i)}>
                  {previewItemLabel(it, i)}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <span data-testid="prompt-test-preview-label" className="text-xs text-text-muted">
            {active ? previewItemLabel(active, 0) : ''}
          </span>
        )}
        <span data-testid="prompt-test-preview-pipeline" className="text-[11px] text-text-muted">
          {COPY.pipeline(preview.pipeline)}
        </span>
      </div>

      {active ? (
        <PreviewItemView item={active} />
      ) : (
        <p data-testid="prompt-test-preview-empty" className="text-xs text-text-muted">
          {COPY.previewEmpty}
        </p>
      )}

      {/* What the live pipeline adds that a replay cannot — said out loud. */}
      {preview.note ? (
        <p
          data-testid="prompt-test-preview-note"
          className="rounded border-l-4 border-info bg-info/10 px-3 py-2 text-[11px] leading-relaxed text-text"
        >
          {preview.note}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// E05-F09-T03 — the two answers, each with its own numbers
// ---------------------------------------------------------------------------

/**
 * Model / tokens / time of ONE call, labelled with which call it describes.
 *
 * Deliberately NOT a single header line for the whole window: the test ran just
 * now on today's model, the recorded run ran whenever it ran on whatever model
 * was configured then, and the admin is here precisely to compare the two. One
 * shared numbers line would attribute the test's model and duration to
 * production's answer.
 */
function CallStats({
  stats,
  side,
}: {
  readonly stats: PromptCallStats;
  readonly side: 'test' | 'production';
}): JSX.Element {
  return (
    <div
      data-testid="prompt-test-stats"
      data-side={side}
      className="flex flex-wrap items-center gap-3 text-[11px] text-text-muted"
    >
      <span className="uppercase tracking-wide">{COPY.statsSide[side]}</span>
      <span>
        {COPY.stats.model}:{' '}
        <span data-testid="prompt-test-stats-model" className="font-mono text-text-strong">
          {stats.model || '—'}
        </span>
      </span>
      <span>
        {COPY.stats.tokens}:{' '}
        <span data-testid="prompt-test-stats-tokens" className="text-text-strong">
          {formatTokens(stats.inputTokens, stats.outputTokens)}
        </span>
      </span>
      <span>
        {COPY.stats.duration}:{' '}
        <span data-testid="prompt-test-stats-duration" className="text-text-strong">
          {formatDurationOrDash(stats.durationMs)}
        </span>
      </span>
    </div>
  );
}

/**
 * One answer, rendered from its {@link PromptAnswerState}. Used by BOTH the
 * "Raw answer" tab and the "What production got" tab, which is the point: the
 * comparison is only usable if the same situation is described the same way on
 * both sides.
 *
 * Every "nothing to show" case is a sentence, never a blank box — a failed
 * parse and an empty answer are the two findings this window exists to name
 * (T03 acceptance), and an empty panel is exactly how they look on the live
 * screens the admin came here from.
 */
function AnswerBody({
  state,
  prefix,
  isTest,
}: {
  readonly state: PromptAnswerState;
  readonly prefix: string;
  readonly isTest: boolean;
}): JSX.Element {
  if (state.kind === 'call-failed') {
    return (
      <div
        data-testid={`${prefix}-error`}
        className="rounded border-l-4 border-danger bg-danger/10 px-3 py-2"
      >
        <p className="text-xs font-semibold text-text-strong">{COPY.callFailed}</p>
        <p className={`mt-1 whitespace-pre-wrap break-words text-text ${MONO}`}>{state.error}</p>
        <p className="mt-1 text-[11px] text-text-muted">
          {isTest ? COPY.callFailedHint : COPY.productionFailedHint}
        </p>
      </div>
    );
  }

  if (state.kind === 'no-answer') {
    return (
      <p data-testid={`${prefix}-none`} className="text-xs text-text-muted">
        {COPY.answerNone}
      </p>
    );
  }

  if (state.kind === 'empty-answer') {
    return (
      <div
        data-testid={`${prefix}-empty`}
        className="rounded border-l-4 border-warning bg-warning/10 px-3 py-2"
      >
        <p className="text-xs font-semibold text-text-strong">{COPY.answerEmpty}</p>
        <p className="mt-1 text-[11px] text-text-muted">{COPY.answerEmptyHint}</p>
      </div>
    );
  }

  return (
    <div className="grid gap-2">
      {state.kind === 'parse-failed' ? (
        <div
          data-testid={`${prefix}-parse-failed`}
          className="rounded border-l-4 border-warning bg-warning/10 px-3 py-2"
        >
          <p className="text-xs font-semibold text-text-strong">{COPY.parseFailed}</p>
          {state.parseError ? (
            <p className={`mt-1 whitespace-pre-wrap break-words text-text ${MONO}`}>
              {state.parseError}
            </p>
          ) : null}
          <p className="mt-1 text-[11px] text-text-muted">{COPY.parseFailedHint}</p>
        </div>
      ) : null}
      <pre
        data-testid={`${prefix}-text`}
        className={`whitespace-pre-wrap break-words text-text ${MONO}`}
      >
        {state.text}
      </pre>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Result section (three tabs + stats)
// ---------------------------------------------------------------------------

type ResultTab = 'user' | 'raw' | 'production';

/**
 * Exported for the component test (`PromptTestDialog.test.tsx`): the three
 * result tabs are the acceptance of T03/T04 and take only plain data, so they
 * are asserted directly instead of through the dialog's three network hooks.
 */
export function ResultSection({
  result,
  run,
}: {
  readonly result: PromptTestResult | null;
  readonly run: PromptRunDetail;
}): JSX.Element {
  const [tab, setTab] = useState<ResultTab>('user');

  const tabs: readonly { readonly id: ResultTab; readonly label: string }[] = [
    { id: 'user', label: COPY.tabs.user },
    { id: 'raw', label: COPY.tabs.raw },
    { id: 'production', label: COPY.tabs.production },
  ];

  // Both answers are classified by the SAME function, so "empty" / "parse
  // failed" cannot mean one thing on the test side and another on the recorded
  // side (see `describeAnswer`).
  const testAnswer: PromptAnswerState | null =
    result === null
      ? null
      : describeAnswer({
          text: result.rawText,
          error: result.error,
          parseOk: result.parseOk,
          parseError: result.parseError,
        });
  // The ONE decision of the "As the user sees it" tab, taken by the pure model
  // (`chooseUserTabContent`) rather than inline: it is the seam where the whole
  // feature can be silently un-done, so it is unit-tested without a DOM.
  const userContent: UserTabContent | null = result === null ? null : chooseUserTabContent(result);

  const productionAnswer = describeAnswer({
    text: run.responseText,
    error: run.error,
    // `parseOk` of the journal row is what the pipeline recorded AT THE TIME —
    // this is how the answer was actually read in production, which is the fact
    // being compared against.
    parseOk: run.parseOk,
  });

  return (
    <section data-testid="prompt-test-result" className={PANEL}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-surface-muted px-3 py-2">
        <div role="tablist" aria-label="Test result" className="flex gap-1">
          {tabs.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`prompt-test-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`rounded px-3 py-1.5 text-xs font-medium ${
                  active
                    ? 'bg-primary text-white'
                    : 'text-text-muted hover:bg-white hover:text-text-strong'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* The numbers follow the tab: production's answer never gets the test
            call's model / tokens / duration hung above it. */}
        {tab === 'production' ? (
          <CallStats stats={recordedRunStats(run)} side="production" />
        ) : result ? (
          <CallStats stats={testCallStats(result)} side="test" />
        ) : null}
      </div>

      <div role="tabpanel" className="max-h-[46vh] overflow-auto px-3 py-3">
        {tab === 'production' ? (
          /* "What production got": the answer of the SELECTED RECORDED RUN,
             with that run's own model / tokens / time in the header above. */
          <div data-testid="prompt-test-panel-production" className="grid gap-2">
            <p data-testid="prompt-test-production-label" className="text-[11px] text-text-muted">
              {runLabel(run)} · {originLabel(run.origin)}
            </p>
            <AnswerBody state={productionAnswer} prefix="prompt-test-production" isTest={false} />
          </div>
        ) : result === null || testAnswer === null ? (
          <p
            data-testid="prompt-test-result-placeholder"
            className="py-6 text-center text-xs text-text-muted"
          >
            {COPY.resultPlaceholder}
          </p>
        ) : tab === 'raw' ? (
          /* The model's answer BEFORE parsing — the text the parser was given. */
          <div data-testid="prompt-test-panel-raw">
            <AnswerBody state={testAnswer} prefix="prompt-test-raw" isTest />
          </div>
        ) : (
          <div data-testid="prompt-test-panel-user" className="grid gap-2">
            {/* A failed call / a blank answer are stated here too: this tab is
                the one that would otherwise render as an empty screen. */}
            {testAnswer.kind === 'call-failed' ||
            testAnswer.kind === 'empty-answer' ||
            testAnswer.kind === 'no-answer' ? (
              <AnswerBody state={testAnswer} prefix="prompt-test-user" isTest />
            ) : (
              <>
                {testAnswer.kind === 'parse-failed' ? (
                  <div
                    data-testid="prompt-test-parse-failed"
                    className="rounded border-l-4 border-warning bg-warning/10 px-3 py-2"
                  >
                    <p className="text-xs font-semibold text-text-strong">{COPY.parseFailed}</p>
                    <p className="mt-1 text-[11px] text-text-muted">{COPY.parseFailedHint}</p>
                  </div>
                ) : null}
                {result.parseOk === null ? (
                  <p className="text-[11px] text-text-muted">{COPY.parseNotApplicable}</p>
                ) : null}
                {/* WHAT to draw is decided by `chooseUserTabContent`, and it does
                    NOT look at `parseOk`: a prompt whose `output_format` claims
                    plain text (`search_salary_rate`) still gets a full
                    production cleanup server-side, and gating this tab on the
                    generic parse verdict showed its RAW answer here — identical
                    to the "Raw answer" tab (E05-F09-T04 regression). */}
                {userContent?.kind === 'raw' ? (
                  /* No preview and no parse (an older server): the answer
                     verbatim — still a rendering, never an empty panel. */
                  <pre
                    data-testid="prompt-test-user-raw"
                    className={`whitespace-pre-wrap break-words text-text ${MONO}`}
                  >
                    {userContent.text}
                  </pre>
                ) : (
                  /* The cleaned answer comes from the SERVER (`result.preview`,
                     built by calling the pipelines' own parse + cleanup);
                     `parsed` is only the fallback for a response that carries no
                     preview at all. */
                  <UserResultView
                    preview={userContent?.kind === 'preview' ? userContent.preview : null}
                    parsed={result.parsed}
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// The dialog
// ---------------------------------------------------------------------------

export interface PromptTestDialogProps {
  readonly prompt: AdminPromptDTO;
  readonly onClose: () => void;
}

export function PromptTestDialog({ prompt, onClose }: PromptTestDialogProps): JSX.Element {
  const promptKey = prompt.prompt_key;
  const toast = useToast();
  const runsQuery = usePromptRuns(promptKey);
  const [runId, setRunId] = useState<string | null>(null);
  const runQuery = usePromptRun(promptKey, runId);
  const testPrompt = useTestPrompt();
  const updatePrompt = useUpdatePrompt();

  const [draft, setDraft] = useState<string>(prompt.prompt_template);
  const [result, setResult] = useState<PromptTestResult | null>(null);

  /**
   * The template as it currently stands IN THE DATABASE, as far as this window
   * knows. Not read straight off `prompt.prompt_template`: the parent hands the
   * dialog the row object it had when the Test button was pressed and keeps
   * holding it, so after a successful save that prop would still carry the OLD
   * text and the "edited" badge would never clear (nor would the Save button
   * re-disable). Saving updates this; the prop only seeds it.
   */
  const [savedTemplate, setSavedTemplate] = useState<string>(prompt.prompt_template);

  const runs = useMemo(() => runsQuery.data ?? [], [runsQuery.data]);

  // Default to the newest run once the list arrives; a run that disappeared
  // from the ring buffer falls back to the newest one rather than to nothing.
  useEffect(() => {
    if (runs.length === 0) return;
    setRunId((prev) =>
      prev !== null && runs.some((r) => r.id === prev) ? prev : (runs[0]?.id ?? null),
    );
  }, [runs]);

  const run = runQuery.data ?? null;

  const values = useMemo(() => (run ? varsToMap(run.vars) : {}), [run]);
  // Memoised, not `run?.defaults ?? {}` inline: a fresh `{}` on every render
  // would re-run the rebuild below on every keystroke ANYWHERE in the dialog.
  const defaults = useMemo(() => run?.defaults ?? {}, [run]);
  const renderedPrompt = useMemo(
    () => substitutePromptVars(draft, values, defaults),
    [draft, values, defaults],
  );
  // Derived from the RENDERED text, exactly as the server derives the
  // `missingVars` it returns — see `findMissingVars` in the model.
  const missingVars = useMemo(
    () => findMissingVars(draft, renderedPrompt),
    [draft, renderedPrompt],
  );
  const unusedVars = useMemo(() => (run ? findUnusedVars(draft, run.vars) : []), [draft, run]);

  const isDirty = draft !== savedTemplate;
  const hasRuns = runs.length > 0;

  function handleTest(): void {
    if (!run) return;
    setResult(null);
    testPrompt.mutate(
      { promptKey, runId: run.id, template: draft },
      {
        onSuccess: (res) => setResult(res),
        onError: (error) => toast.error(errorMessage(error, 'Test run failed')),
      },
    );
  }

  function handleSave(): void {
    // The ONLY path in this window that writes `ai_prompts`. Snapshot what was
    // sent so "edited" reflects the draft against what is now stored.
    const saving = draft;
    updatePrompt.mutate(
      { promptKey, patch: { prompt_template: saving } },
      {
        onSuccess: () => {
          setSavedTemplate(saving);
          toast.success(COPY.saved);
        },
        onError: (error) => toast.error(errorMessage(error, 'Save error')),
      },
    );
  }

  const footer = (
    <div className="flex w-full flex-wrap items-center justify-between gap-3">
      <span data-testid="prompt-test-run-note" className="text-[11px] text-text-muted">
        {hasRuns ? COPY.runNote : COPY.emptyHint}
      </span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          data-testid="prompt-test-close"
          onClick={onClose}
          className="rounded border border-border bg-white px-4 py-2 text-sm font-medium text-text-strong hover:bg-gray-50"
        >
          {COPY.close}
        </button>
        <button
          type="button"
          data-testid="prompt-test-save"
          onClick={handleSave}
          disabled={!isDirty || updatePrompt.isPending}
          className="rounded border border-border bg-white px-4 py-2 text-sm font-medium text-text-strong hover:bg-gray-50 disabled:opacity-50"
        >
          {updatePrompt.isPending ? COPY.saving : COPY.save}
        </button>
        <button
          type="button"
          data-testid="prompt-test-run"
          onClick={handleTest}
          disabled={!hasRuns || run === null || testPrompt.isPending}
          className="rounded bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-50"
        >
          {testPrompt.isPending ? COPY.running : COPY.run}
        </button>
      </div>
    </div>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${COPY.title} — ${prompt.title || promptKey}`}
      maxWidth="7xl"
      testId="prompt-test-dialog"
      footer={footer}
    >
      <div className="grid gap-4">
        {/* Header: run selector + prompt meta */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <label htmlFor="prompt-test-run-select" className="block min-w-[320px] flex-1">
            <span className="mb-1 block text-xs font-medium text-text-strong">
              {COPY.runSelector}
            </span>
            <select
              id="prompt-test-run-select"
              data-testid="prompt-test-run-select"
              className="w-full rounded border border-border px-3 py-2 font-mono text-xs focus:border-primary focus:outline-none disabled:opacity-50"
              value={runId ?? ''}
              disabled={!hasRuns}
              onChange={(e) => {
                setRunId(e.target.value);
                setResult(null);
              }}
            >
              {hasRuns ? null : <option value="">—</option>}
              {runs.map((r) => (
                <option key={r.id} value={r.id}>
                  {runLabel(r)}
                </option>
              ))}
            </select>
          </label>

          <div className="flex flex-wrap items-center gap-2 pb-2">
            <span className="font-mono text-[11px] text-text-muted">{promptKey}</span>
            {usesWebSearch(promptKey) ? (
              <Badge tone="info" testId="prompt-test-websearch-badge" title={COPY.webSearchHint}>
                {COPY.webSearch}
              </Badge>
            ) : null}
            {run ? (
              <>
                <Badge
                  tone={run.origin === 'test' ? 'warn' : 'muted'}
                  testId="prompt-test-run-origin"
                >
                  {originLabel(run.origin)}
                </Badge>
                <Badge
                  tone={
                    runStatus(run) === 'ok' ? 'ok' : runStatus(run) === 'error' ? 'danger' : 'warn'
                  }
                  testId="prompt-test-run-status"
                >
                  {runStatus(run)}
                </Badge>
              </>
            ) : null}
          </div>
        </div>

        {runsQuery.isLoading ? (
          <p data-testid="prompt-test-loading" className="py-6 text-center text-xs text-text-muted">
            Loading…
          </p>
        ) : !hasRuns ? (
          /* Scenario 4 — an honest empty state, NOT an empty form. */
          <div
            data-testid="prompt-test-empty"
            className="rounded-lg border border-border bg-surface-muted px-4 py-6 text-center"
          >
            <p className="text-sm font-semibold text-text-strong">{COPY.emptyTitle}</p>
            <p className="mx-auto mt-2 max-w-2xl text-xs leading-relaxed text-text-muted">
              {COPY.emptyBody}
            </p>
          </div>
        ) : (
          <>
            {/* Drift warnings — before the call, not after it. */}
            {missingVars.length > 0 ? (
              <div
                data-testid="prompt-test-missing-vars"
                className="rounded border-l-4 border-warning bg-warning/10 px-3 py-2"
              >
                <p className="text-xs font-semibold text-text-strong">{COPY.missingTitle}</p>
                <p className="mt-1 flex flex-wrap gap-1.5">
                  {missingVars.map((name) => (
                    <span
                      key={name}
                      data-testid="prompt-test-missing-var"
                      data-var-name={name}
                      className="rounded bg-white px-1.5 py-0.5 font-mono text-[11px] text-text-strong"
                    >
                      {`{{${name}}}`}
                    </span>
                  ))}
                </p>
                <p className="mt-1 text-[11px] text-text-muted">{COPY.missingBody}</p>
              </div>
            ) : null}

            <div className="grid gap-4 lg:grid-cols-2">
              {/* Left: draft + recorded values */}
              <div className="grid gap-4">
                <section className={PANEL}>
                  <div className={PANEL_HEAD}>
                    <span className={PANEL_TITLE}>{COPY.draft}</span>
                    {isDirty ? (
                      <Badge tone="warn" testId="prompt-test-draft-dirty">
                        edited
                      </Badge>
                    ) : null}
                  </div>
                  <textarea
                    data-testid="prompt-test-template"
                    rows={14}
                    className={`w-full resize-y border-0 px-3 py-2 text-text focus:outline-none ${MONO}`}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                  />
                  <p className="border-t border-border px-3 py-1.5 text-[11px] text-text-muted">
                    {COPY.draftHint}
                  </p>
                </section>

                <section className={PANEL}>
                  <div className={PANEL_HEAD}>
                    <span className={PANEL_TITLE}>{COPY.values}</span>
                    <span className="text-[10px] text-text-muted">{run ? run.vars.length : 0}</span>
                  </div>
                  <div data-testid="prompt-test-vars" className="max-h-72 overflow-auto">
                    {run && run.vars.length > 0 ? (
                      run.vars.map((v) => (
                        <VarRow
                          key={v.name}
                          name={v.name}
                          value={v.value}
                          chars={v.originalChars}
                          truncatedChars={v.truncatedChars}
                        />
                      ))
                    ) : (
                      <p className="px-3 py-3 text-xs text-text-muted">{COPY.valuesEmpty}</p>
                    )}
                  </div>
                  {unusedVars.length > 0 ? (
                    <p
                      data-testid="prompt-test-unused-vars"
                      className="border-t border-border px-3 py-1.5 text-[11px] text-text-muted"
                    >
                      {COPY.unusedTitle}: {unusedVars.map((n) => `{{${n}}}`).join(', ')}
                    </p>
                  ) : null}
                </section>
              </div>

              {/* Right: the prompt that will actually be sent */}
              <section className={PANEL}>
                <div className={PANEL_HEAD}>
                  <span className={PANEL_TITLE}>{COPY.rendered}</span>
                  <span
                    data-testid="prompt-test-rendered-size"
                    className="text-[10px] text-text-muted"
                  >
                    {formatChars(renderedPrompt.length)}
                  </span>
                </div>
                <pre
                  data-testid="prompt-test-rendered"
                  className={`max-h-[32rem] overflow-auto whitespace-pre-wrap break-words px-3 py-2 text-text ${MONO}`}
                >
                  {renderedPrompt}
                </pre>
                <p className="border-t border-border px-3 py-1.5 text-[11px] text-text-muted">
                  {COPY.renderedHint}
                </p>
                {run ? (
                  <details data-testid="prompt-test-sent-then" className="border-t border-border">
                    <summary className="cursor-pointer px-3 py-1.5 text-[11px] text-link hover:underline">
                      {COPY.sentThen}
                    </summary>
                    <pre
                      className={`max-h-64 overflow-auto whitespace-pre-wrap break-words bg-surface-muted px-3 py-2 text-text ${MONO}`}
                    >
                      {run.renderedPrompt}
                    </pre>
                  </details>
                ) : null}
              </section>
            </div>

            {run ? <ResultSection result={result} run={run} /> : null}
          </>
        )}
      </div>
    </Dialog>
  );
}

export default PromptTestDialog;
